import React, { PropsWithChildren, createContext, useContext } from 'react';
import { chatApi } from 'api/chat';
import { useLongPollingQuery } from './useLP';

const ChatContext = createContext<{
	subscribe: (eventName: string, listener: any) => void;
	unsubscribe: (eventName: string, listener: any) => void;
	currentProgramMembershipId: number | null;
	globalDisabled?: boolean;
}>({
	subscribe: (eventName: string, listener: any) => {},
	unsubscribe: (eventName: string, listener: any) => {},
	currentProgramMembershipId: null,
	globalDisabled: true,
});

export const CHAT_THREAD_KEY = 'chat-thread';
export const CHAT_THREAD_SELF_KEY = 'chat-thread-self';

type ChatContextProviderProps = PropsWithChildren<{
	currentProgramMembershipId: number | null;
	globalDisabled?: boolean;
}>;

// eslint-disable-next-line @typescript-eslint/ban-types
export const ChatContextProvider = ({
	currentProgramMembershipId,
	globalDisabled,
	children,
	...props
}: ChatContextProviderProps) => {
	const subscribe = (eventName: string, listener: any) => {
		document.addEventListener(eventName, listener);
	};

	const unsubscribe = (eventName: string, listener: any) => {
		document.removeEventListener(eventName, listener);
	};

	useLongPollingQuery(
		['sub-chat-thread', Number(currentProgramMembershipId)],
		({ signal }) =>
			chatApi.subChatThread({ params: { programMembershipId: Number(currentProgramMembershipId) }, signal }),
		{
			enabled: Boolean(currentProgramMembershipId),
			onMessage: (data) => {
				const event = new CustomEvent(CHAT_THREAD_KEY, { detail: data });
				document.dispatchEvent(event);
			},
		}
	);

	useLongPollingQuery(
		['sub-chat-thread-self', Number(currentProgramMembershipId)],
		({ signal }) =>
			chatApi.subChatThreadSelf({
				params: { programMembershipId: Number(currentProgramMembershipId) },
				signal,
			}),
		{
			enabled: Boolean(currentProgramMembershipId),
			onMessage: (data) => {
				const event = new CustomEvent(CHAT_THREAD_SELF_KEY, { detail: data });
				document.dispatchEvent(event);
			},
		}
	);

	return (
		<ChatContext.Provider
			value={{ subscribe, unsubscribe, currentProgramMembershipId, globalDisabled: globalDisabled || false }}
			{...props}
		>
			{children}
		</ChatContext.Provider>
	);
};

export const useChatContext = () => useContext(ChatContext);
